import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { notificationService } from '../../services/notificationService';
import { userService } from '../../services/userService';
import { 
  NOTIFICATION_TYPE_LABELS,
  RESPONSE_TYPES 
} from '../../constants/notificationConstants';
import { dateUtils } from '../../utils/helpers';
import LoadingSpinner from '../common/LoadingSpinner';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';

const NotificationStatusModal = ({ isOpen, onClose, notification }) => {
  const { isMobile, getResponsiveValue } = useTheme();
  const [loading, setLoading] = useState(true);
  const [recipientStatuses, setRecipientStatuses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const modalSize = getResponsiveValue({
    mobile: 'w-full h-full',
    tablet: 'w-11/12 max-w-5xl max-h-[90vh]',
    desktop: 'w-full max-w-6xl max-h-[90vh]'
  });

  useEffect(() => {
    if (isOpen && notification) {
      loadRecipientStatuses();
    }
  }, [isOpen, notification]);

  const loadRecipientStatuses = async () => {
    try {
      setLoading(true);
      
      // Get all responses for this notification
      const responses = await notificationService.getNotificationResponses(notification.id);
      
      // Get recipient user details
      const recipientUsers = await Promise.all(
        notification.recipientIds.map(userId => userService.getUserById(userId))
      );
      
      // Combine user data with response data
      const statuses = recipientUsers.map(user => {
        const userResponse = responses.find(r => r.userId === user.id);
        const isRead = Math.random() > 0.3; // TODO: Implement actual read tracking
        
        return {
          userId: user.id,
          name: user.fullName,
          email: user.email,
          department: user.department,
          response: userResponse?.response || RESPONSE_TYPES.PENDING,
          respondedAt: userResponse?.respondedAt || null,
          isRead,
          readAt: isRead ? new Date(Date.now() - Math.random() * 86400000) : null // Mock read time
        };
      });
      
      setRecipientStatuses(statuses);
    } catch (error) {
      console.error('Error loading recipient statuses:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter recipients based on search and status
  const filteredRecipients = recipientStatuses.filter(recipient => {
    const matchesSearch = !searchTerm || 
      recipient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      recipient.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      recipient.department.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = !statusFilter || recipient.response === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const configs = {
      [RESPONSE_TYPES.ACCEPT]: { color: 'bg-success-subtle text-success', label: 'Accept' },
      [RESPONSE_TYPES.REJECT]: { color: 'bg-error-subtle text-error', label: 'Reject' },
      [RESPONSE_TYPES.TENTATIVE]: { color: 'bg-warning-subtle text-warning', label: 'Tentative' },
      [RESPONSE_TYPES.PENDING]: { color: 'bg-info-subtle text-info', label: 'Pending' }
    };
    
    const config = configs[status] || configs[RESPONSE_TYPES.PENDING];
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getReadBadge = (isRead) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isRead ? 'bg-success-subtle text-success' : 'bg-warning-subtle text-warning'
      }`}>
        {isRead ? 'Read' : 'Unread'}
      </span>
    );
  };

  const exportData = () => {
    const csvContent = [
      ['Name', 'Email', 'Department', 'Response', 'Responded At', 'Read Status', 'Read At'],
      ...filteredRecipients.map(recipient => [
        recipient.name,
        recipient.email,
        recipient.department,
        recipient.response,
        recipient.respondedAt ? dateUtils.formatDateTime(recipient.respondedAt) : 'N/A',
        recipient.isRead ? 'Read' : 'Unread',
        recipient.readAt ? dateUtils.formatDateTime(recipient.readAt) : 'N/A'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `notification-status-${notification.id}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getResponseStats = () => {
    const total = recipientStatuses.length;
    const accept = recipientStatuses.filter(r => r.response === RESPONSE_TYPES.ACCEPT).length;
    const reject = recipientStatuses.filter(r => r.response === RESPONSE_TYPES.REJECT).length;
    const tentative = recipientStatuses.filter(r => r.response === RESPONSE_TYPES.TENTATIVE).length;
    const pending = recipientStatuses.filter(r => r.response === RESPONSE_TYPES.PENDING).length;
    const read = recipientStatuses.filter(r => r.isRead).length;
    
    return { total, accept, reject, tentative, pending, read };
  };

  if (!isOpen) return null;

  const stats = getResponseStats();

  return (
    <div className="fixed inset-0 bg-bg-overlay z-50 flex items-center justify-center">
      <div className={`${modalSize} bg-surface-elevated rounded-xl shadow-theme-lg border border-border-primary overflow-hidden`}>
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-primary">
          <div>
            <h2 className="text-xl font-semibold text-text-primary">
              Notification Status
            </h2>
            <p className="text-sm text-text-secondary">
              {notification.type === 'meeting' ? notification.meetingHeading : NOTIFICATION_TYPE_LABELS[notification.type]}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-text-tertiary hover:text-text-primary hover:bg-surface-hover rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col h-full" style={{ maxHeight: '80vh' }}>
          
          {/* Stats Summary */}
          <div className="px-6 py-4 border-b border-border-primary bg-surface-secondary">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-text-primary">{stats.total}</div>
                <div className="text-xs text-text-secondary">Total</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-text-primary">{stats.read}</div>
                <div className="text-xs text-text-secondary">Read</div>
              </div>
              {notification.type !== 'general' && (
                <>
                  <div>
                    <div className="text-2xl font-bold text-success">{stats.accept}</div>
                    <div className="text-xs text-text-secondary">Accept</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-warning">{stats.tentative}</div>
                    <div className="text-xs text-text-secondary">Tentative</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-error">{stats.reject}</div>
                    <div className="text-xs text-text-secondary">Reject</div>
                  </div>
                </>
              )}
              <div>
                <div className="text-2xl font-bold text-info">{stats.pending}</div>
                <div className="text-xs text-text-secondary">Pending</div>
              </div>
            </div>
          </div>

          {/* Filters and Export */}
          <div className="px-6 py-4 border-b border-border-primary">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
              <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-4 flex-1">
                <Input
                  placeholder="Search recipients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-base md:max-w-xs"
                />
                
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  options={[
                    { value: '', label: 'All Status' },
                    { value: RESPONSE_TYPES.ACCEPT, label: 'Accept' },
                    { value: RESPONSE_TYPES.TENTATIVE, label: 'Tentative' },
                    { value: RESPONSE_TYPES.REJECT, label: 'Reject' },
                    { value: RESPONSE_TYPES.PENDING, label: 'Pending' }
                  ]}
                  className="input-base md:max-w-xs"
                  placeholder="Filter by status"
                />
              </div>
            </div>
          </div>

          {/* Recipients Table */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <LoadingSpinner size="lg" text="Loading statuses..." />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-surface-secondary sticky top-0">
                    <tr className="border-b border-border-primary">
                      <th className="text-left px-6 py-3 text-xs font-medium text-text-secondary uppercase tracking-wider">
                        Recipient
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-text-secondary uppercase tracking-wider">
                        Department
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-text-secondary uppercase tracking-wider">
                        Read Status
                      </th>
                      {notification.type !== 'general' && (
                        <th className="text-left px-6 py-3 text-xs font-medium text-text-secondary uppercase tracking-wider">
                          Response
                        </th>
                      )}
                      <th className="text-left px-6 py-3 text-xs font-medium text-text-secondary uppercase tracking-wider">
                        Timestamps
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-primary">
                    {filteredRecipients.map(recipient => (
                      <tr key={recipient.userId} className="hover:bg-surface-hover">
                        <td className="px-6 py-4">
                          <div>
                            <div className="font-medium text-text-primary">{recipient.name}</div>
                            <div className="text-sm text-text-secondary">{recipient.email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-text-secondary">
                          {recipient.department}
                        </td>
                        <td className="px-6 py-4">
                          {getReadBadge(recipient.isRead)}
                        </td>
                        {notification.type !== 'general' && (
                          <td className="px-6 py-4">
                            {getStatusBadge(recipient.response)}
                          </td>
                        )}
                        <td className="px-6 py-4 text-sm text-text-secondary">
                          <div className="space-y-1">
                            {recipient.readAt && (
                              <div>Read: {dateUtils.formatDateTime(recipient.readAt)}</div>
                            )}
                            {recipient.respondedAt && (
                              <div>Responded: {dateUtils.formatDateTime(recipient.respondedAt)}</div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredRecipients.length === 0 && !loading && (
                  <div className="text-center py-12">
                    <div className="text-text-secondary">
                      No recipients found matching your criteria
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border-primary bg-surface-secondary">
          <div className="text-sm text-text-secondary">
            Showing {filteredRecipients.length} of {stats.total} recipients
          </div>
          <Button
            variant="outline"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotificationStatusModal;